/**
 * Clips feed – Web: swipe only (no scrolling). One clip at a time; swipe up/down to next/prev.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Dimensions,
  Pressable,
  View,
  type NativeSyntheticEvent,
  type NativeTouchEvent,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {useStudioClipsQuery} from '#/state/queries/studio'
import type {StudioVideo} from '#/lib/api/studio'
import {atoms as a, useTheme, web} from '#/alf'
import {Text} from '#/components/Typography'
import {ChevronTop_Stroke2_Corner0_Rounded as ChevronUp} from '#/components/icons/Chevron'
import {ChevronBottom_Stroke2_Corner0_Rounded as ChevronDown} from '#/components/icons/Chevron'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import {useClipsFeedFullscreen} from '#/state/shell/clips-feed-fullscreen'
import {ClipSingle, type ClipSingleHandles} from '#/screens/Studio/ClipSingle'
import {ClipsCommentsDialog} from '#/screens/Studio/ClipsCommentsDialog'
import * as Dialog from '#/components/Dialog'

const SWIPE_THRESHOLD_PX = 60
/** Desktop: confine clip to a vertical column (e.g. phone-width) instead of full width */
const CLIPS_DESKTOP_MAX_WIDTH = 420

export type ClipsFeedItem = StudioVideo

export function ClipsFeedPage({isPageFocused = true}: {isPageFocused?: boolean} = {}) {
  const {_} = useLingui()
  const t = useTheme()
  const {isDesktop} = useWebMediaQueries()
  const headerOffset = useHeaderOffset()
  const {setHideBottomBar} = useClipsFeedFullscreen()
  const mediaRef = useRef<ClipSingleHandles | null>(null)
  const commentsControl = Dialog.useDialogControl()
  const [commentsVideo, setCommentsVideo] = useState<{id: string; title: string} | null>(null)
  const [isPTR, setIsPTR] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [paused, setPaused] = useState(false)
  const hasUnfocusedOnce = useRef(false)
  const prevFocusedIndex = useRef(0)
  const touchStartY = useRef(0)
  const containerRef = useRef<View>(null)

  const {data: clips = [], isLoading, refetch} = useStudioClipsQuery()

  const pageHeight =
    Dimensions.get('window').height - (headerOffset ?? 0)

  const onOpenComments = useCallback((videoId: string, videoTitle: string) => {
    setCommentsVideo({id: videoId, title: videoTitle})
    commentsControl.open()
  }, [commentsControl])

  // Hide bottom bar only when clips feed is focused, playing, and not paused
  useEffect(() => {
    setHideBottomBar(Boolean(isPageFocused && playing && !paused))
    return () => setHideBottomBar(false)
  }, [isPageFocused, playing, paused, setHideBottomBar])

  // When page gains focus, allow autoplay again (fixes no play when switching to tab or returning)
  useEffect(() => {
    if (isPageFocused) {
      hasUnfocusedOnce.current = false
    }
  }, [isPageFocused])

  // When page loses focus, stop current clip; remember we left
  useEffect(() => {
    if (!isPageFocused) {
      hasUnfocusedOnce.current = true
      prevFocusedIndex.current = currentIndex
      mediaRef.current?.stop()
    }
  }, [isPageFocused, currentIndex])

  // Play current clip when focused: on first focus, when returning to tab, or when user swiped (index changed).
  useEffect(() => {
    if (clips.length === 0 || !isPageFocused) return
    const indexChanged = prevFocusedIndex.current !== currentIndex
    const shouldPlay = !hasUnfocusedOnce.current || indexChanged
    if (!shouldPlay) return
    prevFocusedIndex.current = currentIndex
    const id = requestAnimationFrame(() => {
      mediaRef.current?.play()
    })
    return () => cancelAnimationFrame(id)
  }, [clips, currentIndex, isPageFocused])

  // Stop previous when index changes (ref is for current page only, so we don't have refs for others)
  const goToIndex = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, clips.length - 1))
      setCurrentIndex(clamped)
    },
    [clips.length],
  )

  const onTouchStart = useCallback((e: NativeSyntheticEvent<NativeTouchEvent>) => {
    const touch = e.nativeEvent.touches[0]
    if (touch) touchStartY.current = touch.pageY
  }, [])

  const onTouchEnd = useCallback(
    (e: NativeSyntheticEvent<NativeTouchEvent>) => {
      const touch = e.nativeEvent.changedTouches[0]
      if (!touch) return
      const dy = touchStartY.current - touch.pageY
      if (dy > SWIPE_THRESHOLD_PX) {
        goToIndex(currentIndex + 1)
      } else if (dy < -SWIPE_THRESHOLD_PX) {
        goToIndex(currentIndex - 1)
      }
    },
    [currentIndex, goToIndex],
  )

  // Desktop: wheel to next/prev, no page scroll (non-passive so preventDefault works)
  useEffect(() => {
    const node = containerRef.current as unknown as HTMLElement | null
    if (!node?.addEventListener) return
    const handler = (e: WheelEvent) => {
      if (e.deltaY > 20) {
        e.preventDefault()
        goToIndex(currentIndex + 1)
      } else if (e.deltaY < -20) {
        e.preventDefault()
        goToIndex(currentIndex - 1)
      }
    }
    node.addEventListener('wheel', handler, {passive: false})
    return () => node.removeEventListener('wheel', handler)
  }, [currentIndex, goToIndex])

  useEffect(() => {
    if (clips.length > 0 && currentIndex >= clips.length) {
      setCurrentIndex(clips.length - 1)
    }
  }, [clips.length, currentIndex])

  // Web: keep screen awake while viewing clips (Screen Wake Lock API)
  useEffect(() => {
    if (clips.length === 0 || typeof navigator === 'undefined' || !navigator.wakeLock) return
    let sentinel: {release: () => Promise<void>} | null = null
    const request = async () => {
      try {
        sentinel = await navigator.wakeLock.request('screen')
      } catch {}
    }
    request()
    return () => {
      sentinel?.release?.()
    }
  }, [clips.length])

  const onRefresh = useCallback(async () => {
    setIsPTR(true)
    try {
      await refetch()
    } finally {
      setIsPTR(false)
    }
  }, [refetch])

  if (isLoading && clips.length === 0) {
    return (
      <View style={[a.flex_1, a.justify_center, a.align_center, a.p_lg]}>
        <ActivityIndicator size="large" />
        <Text style={[a.pt_md, a.text_md, t.atoms.text_contrast_medium]}>
          <Trans>Loading clips…</Trans>
        </Text>
      </View>
    )
  }

  if (!isLoading && clips.length === 0) {
    return (
      <View style={[a.flex_1, a.justify_center, a.align_center, a.p_lg]}>
        <VideoIcon width={48} fill={t.palette.primary_500} />
        <Text style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_semi_bold, t.atoms.text]}>
          <Trans>No clips yet</Trans>
        </Text>
        <Text
          style={[a.text_md, a.pb_xl, a.text_center, t.atoms.text_contrast_medium, {maxWidth: 320}]}>
          <Trans>
            Upload vertical videos as &quot;Clips&quot; in Studio, or check back later.
          </Trans>
        </Text>
      </View>
    )
  }

  const item = clips[currentIndex]
  if (!item) return null

  const atTop = currentIndex <= 0
  const atBottom = currentIndex >= clips.length - 1

  return (
    <>
      <View
        style={[
          a.flex_1,
          a.align_center,
          a.justify_center,
          web({width: '100%'}),
        ]}
      >
      <View
        ref={containerRef}
        style={[
          a.flex_1,
          {height: pageHeight, backgroundColor: t.palette.black},
          isDesktop && web({
            maxWidth: CLIPS_DESKTOP_MAX_WIDTH,
            width: '100%',
            alignSelf: 'center',
          }),
        ]}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <View style={[{width: '100%', height: '100%'}]}>
          <ClipSingle
            ref={(r) => {
              mediaRef.current = r
            }}
            item={item}
            onOpenComments={onOpenComments}
            onPlayStateChange={(p, pa) => {
              setPlaying(p)
              setPaused(pa)
            }}
          />
        </View>

        {/* Desktop only: up/down arrows, hidden at first/last clip */}
        {isDesktop && (
          <>
            {!atTop && (
              <View
                style={[
                  a.absolute,
                  {top: 16, left: 0, right: 0, alignItems: 'center'},
                ]}
                pointerEvents="box-none"
              >
                <Pressable
                  onPress={() => goToIndex(currentIndex - 1)}
                  style={[
                    a.align_center,
                    a.justify_center,
                    {
                      width: 48,
                      height: 48,
                      borderRadius: 24,
                      backgroundColor: 'rgba(0,0,0,0.4)',
                    },
                    web({cursor: 'pointer'}),
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={_(msg`Previous clip`)}
                >
                  <ChevronUp
                    width={28}
                    height={28}
                    style={{color: t.palette.contrast_0}}
                    aria-hidden
                  />
                </Pressable>
              </View>
            )}
            {!atBottom && (
              <View
                style={[
                  a.absolute,
                  {bottom: 16, left: 0, right: 0, alignItems: 'center'},
                ]}
                pointerEvents="box-none"
              >
                <Pressable
                  onPress={() => goToIndex(currentIndex + 1)}
                  style={[
                    a.align_center,
                    a.justify_center,
                    {
                      width: 48,
                      height: 48,
                      borderRadius: 24,
                      backgroundColor: 'rgba(0,0,0,0.4)',
                    },
                    web({cursor: 'pointer'}),
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={_(msg`Next clip`)}
                >
                  <ChevronDown
                    width={28}
                    height={28}
                    style={{color: t.palette.contrast_0}}
                    aria-hidden
                  />
                </Pressable>
              </View>
            )}
          </>
        )}
      </View>
      </View>
      <ClipsCommentsDialog
        control={commentsControl}
        videoId={commentsVideo?.id ?? null}
        videoTitle={commentsVideo?.title ?? ''}
      />
    </>
  )
}
